import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import defaultImage from "../../../utils/DefaultImage";

const EditWineQuantityModal = ({
    isOpen,
    wineItem,
    onClose,
    onSave,
    isSaving = false,
}) => {
    const [quantity, setQuantity] = useState(1);
    const [error, setError] = useState("");

    useEffect(() => {
        if (wineItem) {
            const initialQty = parseInt(wineItem.quantity, 10);
            setQuantity(!isNaN(initialQty) && initialQty > 0 ? initialQty : 1);
            setError("");
        }
    }, [wineItem, isOpen]);

    if (!isOpen || !wineItem) return null;

    const wine = wineItem.wine || {};

    const handleQuantityChange = (e) => {
        const val = e.target.value;
        if (val === "") {
            setQuantity("");
            setError("La quantità è obbligatoria.");
            return;
        }

        // Only digits allowed, max 3 digits (1-999)
        const digitsOnly = val.replace(/\D/g, "");
        if (!digitsOnly) {
            setQuantity("");
            setError("La quantità è obbligatoria.");
            return;
        }

        // Automatically cap at 3 digits (max 999)
        const capped = digitsOnly.slice(0, 3);
        const num = parseInt(capped, 10);

        if (num < 1) {
            setQuantity("");
            setError("La quantità minima è 1.");
        } else {
            setQuantity(num);
            setError("");
        }
    };

    const handleIncrement = () => {
        const current = parseInt(quantity, 10) || 0;
        if (current < 999) {
            setQuantity(current + 1);
            setError("");
        }
    };

    const handleDecrement = () => {
        const current = parseInt(quantity, 10) || 1;
        if (current > 1) {
            setQuantity(current - 1);
            setError("");
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const num = parseInt(quantity, 10);
        if (isNaN(num) || num < 1) {
            setError("Inserisci una quantità valida (minimo 1).");
            return;
        }
        if (num > 999) {
            setError("La quantità non può superare 999.");
            return;
        }
        onSave(wineItem, num);
    };

    const cleanImageUrl = (url) => {
        if (!url || url === "null") return null;
        let clean = url;
        if (clean.startsWith("[")) {
            clean = clean.split('"]')[0].replace('["', '');
        }
        return clean;
    };

    const isSaveDisabled = isSaving || Boolean(error) || !quantity;
    const isDecDisabled = isSaving || (parseInt(quantity, 10) || 1) <= 1;
    const isIncDisabled = isSaving || (parseInt(quantity, 10) || 0) >= 999;

    return (
        <div 
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
            onClick={() => !isSaving && onClose()}
        >
            <div 
                className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden border border-[#8B2433]/20 flex flex-col transform transition-all"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-[#8B2433] px-6 py-4 flex justify-between items-center text-white">
                    <h3 className="text-lg font-semibold poppins-medium">Modifica Quantità Vino</h3>
                    <button 
                        type="button"
                        disabled={isSaving}
                        className={`text-white text-2xl font-bold leading-none transition-colors ${
                            isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-200 cursor-pointer'
                        }`}
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleFormSubmit} className="p-6 flex flex-col gap-5">
                    {/* Wine Info Card */}
                    <div className="bg-[#FBFBFB] p-4 rounded-xl border border-[#8B2433]/15 flex items-start gap-4">
                        <div className="w-14 h-16 bg-white rounded-lg p-1 border border-[#8B2433]/20 flex items-center justify-center shrink-0">
                            <img 
                                src={cleanImageUrl(wine.image) || defaultImage} 
                                alt={wine.name || "Wine"} 
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = defaultImage;
                                }}
                            />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 poppins-medium break-words select-text">
                                {wine.name || "Vino"}
                            </h4>
                            {wine.producer && (
                                <p className="text-xs text-gray-500 poppins-regular break-words mt-1 select-text">
                                    {wine.producer}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Quantity Field */}
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700 poppins-medium flex items-center justify-between">
                            <span>Quantità Bottiglie <span className="text-[#8B2433]">*</span></span>
                            <span className="text-xs text-gray-400 font-normal">Max 3 cifre (1 - 999)</span>
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleDecrement}
                                disabled={isDecDisabled}
                                className={`w-10 h-10 rounded-lg border border-[#8B2433]/30 bg-white text-[#8B2433] font-bold text-lg flex items-center justify-center transition-all ${
                                    isDecDisabled
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:bg-[#8B2433]/10 active:scale-95 cursor-pointer'
                                }`}
                            >
                                -
                            </button>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={quantity}
                                onChange={handleQuantityChange}
                                disabled={isSaving}
                                autoFocus
                                className={`flex-1 h-10 px-4 text-center text-lg font-semibold rounded-lg border ${
                                    error ? 'border-red-500 bg-red-50/50' : 'border-[#8B2433]/30 focus:border-[#8B2433]'
                                } focus:outline-none focus:ring-2 focus:ring-[#8B2433]/20 transition-all text-gray-800`}
                            />
                            <button
                                type="button"
                                onClick={handleIncrement}
                                disabled={isIncDisabled}
                                className={`w-10 h-10 rounded-lg border border-[#8B2433]/30 bg-white text-[#8B2433] font-bold text-lg flex items-center justify-center transition-all ${
                                    isIncDisabled
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:bg-[#8B2433]/10 active:scale-95 cursor-pointer'
                                }`}
                            >
                                +
                            </button>
                        </div>
                        {error && (
                            <p className="text-xs text-red-600 poppins-regular mt-1">{error}</p>
                        )}
                    </div>

                    {/* Footer Buttons */}
                    <div className="mt-2 flex justify-end items-center gap-3">
                        <button
                            type="button"
                            disabled={isSaving}
                            onClick={onClose}
                            className={`px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors poppins-regular ${
                                isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
                            }`}
                        >
                            Annulla
                        </button>
                        <button
                            type="submit"
                            disabled={isSaveDisabled}
                            className={`px-6 py-2.5 text-sm font-medium text-white bg-[#8B2433] rounded-lg transition-colors poppins-medium shadow-sm flex items-center gap-2 ${
                                isSaveDisabled
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-[#681723] cursor-pointer'
                            }`}
                        >
                            {isSaving ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    <span>Salvataggio...</span>
                                </>
                            ) : (
                                <span>Salva Modifiche</span>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

EditWineQuantityModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    wineItem: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
};

export default EditWineQuantityModal;

